import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('privacyPolicy.backToHome')}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('privacyPolicy.title')}</h1>
          <p className="text-gray-600">{t('privacyPolicy.lastUpdated')} {new Date().toLocaleDateString(i18n.language)}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {/* Section 1: Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section1.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('privacyPolicy.section1.content')}
            </p>
          </section>

          {/* Section 2: Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section2.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section2.subtitle1')}</h3>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section2.content1')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section2.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section2.subtitle2')}</h3>
            <p className="text-gray-700 mb-3">
              {t('privacyPolicy.section2.content2')}
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section2.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>{t('privacyPolicy.section2.important')}</strong> {t('privacyPolicy.section2.importantText')}
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section2.subtitle3')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section2.list3', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section2.subtitle4')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section2.list4', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 3: How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section3.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section3.content')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section3.list', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 4: Data Storage and Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section4.title')}</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section4.subtitle1')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section4.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section4.subtitle2')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section4.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section4.subtitle3')}</h3>
            <p className="text-gray-700">{t('privacyPolicy.section4.content3')}</p>
          </section>

          {/* Section 5: Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section5.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section5.content')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section5.subtitle1')}</h3>
            <p className="text-gray-700">{t('privacyPolicy.section5.content1')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section5.subtitle2')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section5.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section5.subtitle3')}</h3>
            <p className="text-gray-700">{t('privacyPolicy.section5.content3')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section5.subtitle4')}</h3>
            <p className="text-gray-700">{t('privacyPolicy.section5.content4')}</p>
          </section>

          {/* Section 6: User Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section6.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section6.content')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section6.list', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-gray-700 mt-3">{t('privacyPolicy.section6.contact')}</p>
          </section>

          {/* Section 7: Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section7.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section7.content')}</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section7.subtitle1')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section7.list1', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">{t('privacyPolicy.section7.subtitle2')}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              {t('privacyPolicy.section7.list2', { returnObjects: true }).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <p className="text-gray-700 mt-3">{t('privacyPolicy.section7.note')}</p>
          </section>

          {/* Section 8: Third-Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section8.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section8.content1')}</p>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section8.content2')}</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><a href="https://help.instagram.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('privacyPolicy.section8.links.instagram')}</a></li>
              <li><a href="https://www.facebook.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('privacyPolicy.section8.links.facebook')}</a></li>
              <li><a href="https://twitter.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('privacyPolicy.section8.links.twitter')}</a></li>
              <li><a href="https://www.linkedin.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('privacyPolicy.section8.links.linkedin')}</a></li>
            </ul>
          </section>

          {/* Section 9: Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section9.title')}</h2>
            <p className="text-gray-700">{t('privacyPolicy.section9.content')}</p>
          </section>

          {/* Section 10: Privacy Policy Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section10.title')}</h2>
            <p className="text-gray-700">{t('privacyPolicy.section10.content')}</p>
          </section>

          {/* Section 11: Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.section11.title')}</h2>
            <p className="text-gray-700 mb-3">{t('privacyPolicy.section11.content')}</p>
            <ul className="list-none text-gray-700 space-y-2">
              <li><strong>{t('privacyPolicy.section11.email')}</strong> {t('privacyPolicy.section11.emailValue')}</li>
              <li><strong>{t('privacyPolicy.section11.web')}</strong> {t('privacyPolicy.section11.webValue')}</li>
              <li><strong>{t('privacyPolicy.section11.support')}</strong> {t('privacyPolicy.section11.supportValue')}</li>
            </ul>
          </section>

          {/* KVKK Notice */}
          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacyPolicy.kvkkNotice.title')}</h2>
            <p className="text-gray-700">{t('privacyPolicy.kvkkNotice.content')}</p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-600">
          <p>{t('privacyPolicy.footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
